/**
 * Research AI Service
 * Handles communication with the specialized research AI agent
 */

import { userService } from './userService';

interface CreatorInfo {
  id: string; // Added creator ID field
  name: string;
  handle: string;
  platform: string;
  followers_count?: number;
  engagement_rate?: number;
  handles?: Record<string, string>; // For multi-platform creators: { tiktok: 'handle1', instagram: 'handle2' }
}

interface ResearchRequest {
  prompt: string;
  creator_info: CreatorInfo;
  resources: string[];
  context?: string;
  response_format?: 'one_word' | 'short' | 'detailed';
  data_type?: 'text' | 'boolean' | 'number' | 'currency' | 'percentage' | 'labels';
  platform_resources?: Array<{
    id: string;
    title: string;
    plain_text: string;
    category: string;
  }>;
  label_options?: string[]; // Array of custom alternatives for labels cell type
  project_id?: string; // project_uuid used for auto-including internal resources
  email_provider?: 'outlook' | 'gmail'; // Email provider for routing to conversation-ai-agent
  communication_sources?: ('email_history' | 'outlook_conversations' | 'gmail_conversations' | 'instagram_messages')[]; // Communication sources for routing
  table_id?: string; // Table ID for fetching table-specific context documents
  column_type?: 'normal' | 'communication' | 'relationship' | 'outreach'; // Column type for routing
  target_platform?: 'tiktok' | 'instagram' | 'youtube' | 'twitch'; // Target platform for multi-platform creators
  relationship_config?: { // Relationship column configuration
    prompt: string;
    result_format?: 'number' | 'currency' | 'percentage' | 'text';
    source_columns?: string[];
    currency?: string;
  };
  source_columns?: Record<string, { name: string; value: string }>; // Source column data for calculations
  result_format?: string; // Result format for calculations
  currency?: string; // Currency for currency-formatted calculations
}

interface EmailFinderRequest {
  creator_info: {
    id: string;
    name: string;
    handles: {
      tiktok?: string;
      instagram?: string;
    };
  };
}

interface EmailFinderResponse {
  success: boolean;
  email?: string;
  confidence?: 'high' | 'medium' | 'low';
  reasoning?: string;
  sources?: string[];
  error?: string;
}

interface MessageGenerationRequest {
  ai_prompt: string;
  creator_info: CreatorInfo;
  columns: Array<{ id: string; name: string; value?: string }>;
  resources?: string[];
  context?: string;
}

interface ResearchResponse {
  success: boolean;
  result?: string;
  reasoning?: string;
  source?: string;
  confidence?: 'high' | 'medium' | 'low';
  sources_used?: string[];
  fetch_time?: number;
  error?: string;
  error_type?: 'quota_exhausted' | 'trial_expired' | 'api_error' | 'unknown';
  requests_remaining?: number;
  timestamp?: string;
  // Conversation-specific fields for message timeline display
  messages?: Array<{
    message_id: string;
    user_type: 'USER' | 'CREATOR';
    sender: string;
    subject?: string;
    received_at: string;
    content: string;
    is_target?: boolean;
  }>;
  highlight_text?: string;
  source_message_id?: string;
  context_found_in_message_id?: string;
  // Companion-specific: conversation preview for UI display
  conversation_preview?: Array<{
    message_id: string;
    user_type: 'USER' | 'CREATOR';
    sender: string;
    subject?: string;
    received_date_time?: string;
    content: string;
    has_attachments?: boolean;
  }>;
}

// API Configuration
const PROD_API_BASE = 'https://5z9vmw0tg6.execute-api.eu-north-1.amazonaws.com/production';
const DEV_API_BASE = 'https://9vnb05of2j.execute-api.eu-north-1.amazonaws.com/development';

// Auto-select API by environment
const API_BASE_URL = (() => {
  const fromEnv = process.env.REACT_APP_API_URL;
  if (fromEnv && fromEnv.trim().length > 0) return fromEnv;
  if (typeof window !== 'undefined') {
    const host = window.location.host || '';
    const hostname = window.location.hostname || '';
    const isLocalhost = hostname === 'localhost' || hostname === '127.0.0.1';
    const isDevSite = host.includes('saas-application-dev.s3-website.eu-north-1.amazonaws.com');
    if (isLocalhost || isDevSite) return DEV_API_BASE;
  }
  return PROD_API_BASE;
})();

class ResearchAIService {
  /**
   * Determine which AI agent endpoint to use based on the request
   */
  private determineEndpoint(request: ResearchRequest): string {
    // PRIORITY 0: Check if this is a relationship/calculation column
    if (request.column_type === 'relationship' || request.relationship_config) {
      console.log('🔀 [ROUTING] Using relationship-ai-agent (calculation column detected)');
      return 'research-ai-agent/relationship-ai-agent';
    }
    
    // PRIORITY 1: Check if column is explicitly configured for email/conversations
    if (request.email_provider === 'outlook' || request.email_provider === 'gmail') {
      console.log('🔀 [ROUTING] Using conversation-ai-agent (email_provider detected:', request.email_provider + ')');
      return 'research-ai-agent/conversation-ai-agent';
    }
    
    // PRIORITY 2: Check communication_sources array (most reliable indicator)
    if (request.communication_sources && request.communication_sources.length > 0) {
      console.log('🔀 [ROUTING] Using conversation-ai-agent (communication_sources detected:', request.communication_sources + ')');
      return 'research-ai-agent/conversation-ai-agent';
    }
    
    // PRIORITY 3: Check if resources include conversation sources
    const resources = request.resources || [];
    const conversationResources = ['outlook_conversations', 'email_history', 'gmail_conversations'];
    if (resources.some(r => conversationResources.includes(r))) {
      console.log('🔀 [ROUTING] Using conversation-ai-agent (conversation resource detected:', resources + ')');
      return 'research-ai-agent/conversation-ai-agent';
    }
    
    // Default to platform-ai-agent for all other requests
    // This handles all normal/platform columns with platform_resources
    console.log('🔀 [ROUTING] Using platform-ai-agent (default for platform/social media data)');
    return 'research-ai-agent/platform-ai-agent';
  }

  /**
   * Research a specific question about a creator
   */
  async researchCreator(request: ResearchRequest): Promise<ResearchResponse> {
    try {
      // Debug logging for labels
      if (request.data_type === 'labels') {
        console.log('🏷️ [DEBUG] SERVICE LABELS REQUEST:', {
          data_type: request.data_type,
          label_options: request.label_options,
          prompt: request.prompt?.substring(0, 100) + '...',
          request: request
        });
      }

      const token = await userService.getAuthToken();
      const endpoint = this.determineEndpoint(request);
      
      console.log(`🔀 [ResearchAIService] Routing to ${endpoint} for request:`, {
        prompt: request.prompt?.substring(0, 100) + '...',
        resources: request.resources,
        hasPlatformResources: (request.platform_resources?.length || 0) > 0,
        table_id: request.table_id,
        hasTableId: !!request.table_id,
        target_platform: request.target_platform
      });
      
      console.log('🎯 [TARGET_PLATFORM_DEBUG] target_platform value:', request.target_platform, 'type:', typeof request.target_platform);
      console.log('📤 [ResearchAIService] Full request body being sent to Lambda:', JSON.stringify(request, null, 2));
      
      const response = await fetch(`${API_BASE_URL}/${endpoint}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(request)
      });

      if (!response.ok) {
        // Handle 429 (quota exhausted) specially
        if (response.status === 429) {
          try {
            const errorData = await response.json();
            return {
              success: false,
              error: errorData.error || 'AI request limit reached',
              error_type: 'quota_exhausted',
              requests_remaining: errorData.requests_remaining || 0
            };
          } catch {
            return {
              success: false,
              error: 'AI request limit reached',
              error_type: 'quota_exhausted',
              requests_remaining: 0
            };
          }
        }
        
        // Handle other errors
        const errorText = await response.text();
        throw new Error(`Research API failed: ${response.status} - ${errorText}`);
      }

      const data = await response.json();
      return data;
      
    } catch (error) {
      console.error('Research AI Service error:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error'
      };
    }
  }

  /**
   * Call the post-discovery-agent Lambda for track_posts columns
   */
  async callPostDiscoveryAgent(payload: {
    creator_id: string;
    platform: 'tiktok' | 'instagram' | undefined;
    search_criteria: string | undefined;
    table_id: string | undefined;
    column_id: string;
  }): Promise<ResearchResponse> {
    try {
      // Validate required fields
      if (!payload.platform) {
        throw new Error('Platform is required for post discovery');
      }
      if (!payload.search_criteria) {
        throw new Error('Search criteria is required for post discovery');
      }
      if (!payload.table_id) {
        throw new Error('Table ID is required for post discovery');
      }
      
      const token = await userService.getAuthToken();
      
      console.log('🔍 [POST-DISCOVERY] Calling post-discovery-agent with payload:', payload);
      
      // Create validated payload for the API call
      const validatedPayload = {
        creator_id: payload.creator_id,
        platform: payload.platform,
        search_criteria: payload.search_criteria,
        table_id: payload.table_id,
        column_id: payload.column_id
      };
      
      const response = await fetch(`${API_BASE_URL}/post-discovery-agent`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(validatedPayload)
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('🔍 [POST-DISCOVERY] API error:', response.status, errorText);
        throw new Error(`Post discovery failed: ${response.status} - ${errorText}`);
      }

      const data = await response.json();
      console.log('🔍 [POST-DISCOVERY] Response:', data);
      
      // Transform the response to match ResearchResponse format
      return {
        success: true,
        result: data.status || 'completed',
        reasoning: data.reasoning || 'Post discovery completed',
        confidence: data.confidence || 'medium',
        fetch_time: Date.now()
      };
      
    } catch (error) {
      console.error('🔍 [POST-DISCOVERY] Service error:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Post discovery failed'
      };
    }
  }

  /**
   * Generate AI-drafted message for outreach
   */
  async generateMessage(request: MessageGenerationRequest): Promise<ResearchResponse> {
    try {
      // Transform the message generation request into a research request
      // Create a comprehensive prompt that includes column data context
      const columnContext = request.columns
        .filter(col => col.value)
        .map(col => `${col.name}: ${col.value}`)
        .join('\n');
      
      const enhancedPrompt = `Generate a personalized outreach message based on this request:

"${request.ai_prompt}"

Creator Information:
- Name: ${request.creator_info.name}
- Handle: @${request.creator_info.handle}
- Platform: ${request.creator_info.platform}
- Followers: ${request.creator_info.followers_count?.toLocaleString() || 'N/A'}

Available Column Data:
${columnContext || 'No column data available'}

Generate a professional, personalized message that:
1. Uses the creator's name and relevant details
2. References specific data points from the columns when relevant
3. Maintains a professional but friendly tone
4. Includes clear call-to-action
5. Uses template variables like {{creator_name}}, {{pricing}}, {{verification}} etc. for values that should be dynamic

Return the message as plain text that can be used as an email template with variables.`;

      const researchRequest: ResearchRequest = {
        prompt: enhancedPrompt,
        creator_info: request.creator_info,
        resources: request.resources || [],
        context: request.context,
        response_format: 'detailed',
        data_type: 'text'
      };

      return await this.researchCreator(researchRequest);
      
    } catch (error) {
      console.error('Message generation error:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error'
      };
    }
  }

  /**
   * Batch research multiple creators for the same prompt
   */
  async batchResearch(
    prompt: string,
    creators: CreatorInfo[],
    resources: string[],
    context?: string,
    responseFormat?: 'one_word' | 'short' | 'detailed'
  ): Promise<Array<{ creator: CreatorInfo; result: ResearchResponse }>> {
    const results = [];
    
    // Process sequentially to avoid API rate limits
    for (const creator of creators) {
      const result = await this.researchCreator({
        prompt,
        creator_info: creator,
        resources,
        context,
        response_format: responseFormat
      });
      
      results.push({ creator, result });
      
      // Small delay between requests
      await new Promise(resolve => setTimeout(resolve, 500));
    }
    
    return results;
  }

  /**
   * Find email address for a creator using AI email finder
   */
  async findEmail(creatorInfo: {
    id: string;
    name: string;
    handles: {
      tiktok?: string;
      instagram?: string;
      youtube?: string;
      twitch?: string;
    };
  }): Promise<EmailFinderResponse> {
    try {
      const token = await userService.getAuthToken();
      
      console.log('🔍 [EMAIL-FINDER] Searching for email:', creatorInfo);
      
      const response = await fetch(`${API_BASE_URL}/email-ai-finder`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          creator_info: creatorInfo
        })
      });

      if (!response.ok) {
        throw new Error(`Email finder failed: ${response.status}`);
      }

      const data = await response.json();
      console.log('✅ [EMAIL-FINDER] Response:', data);
      
      return data;
      
    } catch (error) {
      console.error('❌ [EMAIL-FINDER] Error:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error'
      };
    }
  }

  /**
   * Call the companion AI agent to draft messages for creators
   */
  async callCompanionAgent(request: {
    prompt: string;
    table_goal: string;
    creator_info: CreatorInfo;
    creator_emails?: string[];
    project_id?: string;
    table_id?: string;
    column_id?: string;
  }): Promise<ResearchResponse> {
    try {
      const token = await userService.getAuthToken();
      
      console.log('🤖 [COMPANION-AI] Calling companion-ai-agent with request:', request);
      
      const response = await fetch(`${API_BASE_URL}/companion-ai-agent`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(request)
      });

      if (!response.ok) {
        // Handle 429 (quota exhausted) specially
        if (response.status === 429) {
          try {
            const errorData = await response.json();
            return {
              success: false,
              error: errorData.error || 'AI request limit reached',
              error_type: 'quota_exhausted',
              requests_remaining: errorData.requests_remaining || 0
            };
          } catch {
            return {
              success: false,
              error: 'AI request limit reached',
              error_type: 'quota_exhausted',
              requests_remaining: 0
            };
          }
        }
        
        const errorText = await response.text();
        throw new Error(`Companion AI failed: ${response.status} - ${errorText}`);
      }

      const data = await response.json();
      console.log('🤖 [COMPANION-AI] Response:', data);
      
      return data;
      
    } catch (error) {
      console.error('🤖 [COMPANION-AI] Service error:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Companion AI failed'
      };
    }
  }

  /**
   * Get available research resources from API
   */
  async getAvailableResources(): Promise<Array<{ id: string; name: string }>> {
    try {
      const response = await fetch(`${API_BASE_URL}/research-ai-agent/platform-ai-agent/resources`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`Resources API failed: ${response.status}`);
      }

      const data = await response.json();
      if (data.success && data.resources) {
        return data.resources;
      }
      
      // Fallback to default resources if API fails
      return this.getDefaultResources();
      
    } catch (error) {
      console.error('Failed to fetch resources from API, using defaults:', error);
      return this.getDefaultResources();
    }
  }

  /**
   * Fallback default resources
   */
  private getDefaultResources(): Array<{ id: string; name: string }> {
    return [
      { id: 'tiktok_database', name: 'TikTok Profile' },
      { id: 'tiktok_analytics', name: 'TikTok Analytics' },
      { id: 'instagram_database', name: 'Instagram Profile' },
      { id: 'instagram_analytics', name: 'Instagram Analytics' }
    ];
  }
}

export const researchAIService = new ResearchAIService();
export type { CreatorInfo, ResearchRequest, ResearchResponse, MessageGenerationRequest, EmailFinderRequest, EmailFinderResponse };
